// All portfolio content lives here so the chess and classic views stay in sync.

export const profile = {
  name: 'Rohan Rout',
  role: 'AI Engineering',
  focus: 'Infrastructure',
  tagline: 'Building products that scale to millions',
  intro:
    'Four years building payment systems and aviation platforms at JPMorgan, CGI, and Sabre. MBA from Warwick. Now at Cosine, architecting how coding agents become smarter and more secure.',
  bio:
    'Cloud and Payments specialist with 4 years of experience designing and deploying scalable payment infrastructures for global financial institutions. Combining deep AWS and API integration expertise with business acumen from Warwick MBA to deliver end-to-end product and technical solutions.',
  photo: '/profile-photo.jpg',
  resume: '/resume.pdf',
  email: 'rohan.rout@warwick.ac.uk',
  linkedin: 'https://www.linkedin.com/in/routrohan/',
  calendly: 'https://calendly.com/rohanrout/lets-talk',
};

export const highlights = [
  { value: '100M+', label: 'customers on the payments gateway I owned' },
  { value: '$2.1M', label: 'saved annually through cloud cost optimisation' },
  { value: '12', label: 'features shipped in 18 months at JPMorgan' },
  { value: '30,000+', label: 'profiles searchable at sub-200ms latency' },
];

export const skills = [
  { group: 'Product', items: ['Product roadmaps', 'PRDs', 'User stories', 'GTM strategy', 'Vendor evaluation', 'Stakeholder management'] },
  { group: 'Cloud & Payments', items: ['AWS', 'Terraform', 'Payments systems', 'REST APIs', 'OpenAPI', 'PostgreSQL'] },
  { group: 'AI & Data', items: ['RAG', 'Qdrant', 'LLM inference', 'ETL pipelines', 'Data analytics', 'ML'] },
  { group: 'Languages', items: ['TypeScript', 'Python', 'NodeJS', 'SQL'] },
];

const baseScenarios = [
  {
    id: 9,
    title: "One Permission Model, Three Planes",
    company: "Cosine",
    tags: ["Access Control", "Platform Governance"],
    preview: "Cosine runs across a CLI, a cloud platform and a desktop app. Created one RBAC model to govern access consistently across all three.",
    context: "Cosine's product spans three planes: a CLI, a cloud platform and a desktop app. Each surface is a different way into the same product, so access rules that drift between them quickly become a governance problem. Teams needed a single, consistent answer to 'who is allowed to do this?', no matter which plane an action starts from.",
    challenge: [
      "Define roles and permissions that mean the same thing on the CLI, in the cloud and on desktop",
      "Enforce governance consistently without fragmenting access logic across three surfaces",
      "Keep the model simple enough for admins to understand and audit",
      "Introduce access control without disrupting how people already use the product"
    ],
    approach: [
      "Created an RBAC model for Cosine covering governance across all three planes: CLI, cloud and desktop",
      "Defined a shared set of roles and permissions as the single source of truth for every surface",
      "Mapped actions on each plane to the same permission checks so behaviour stays consistent",
      "Structured the model so new capabilities can be added as permissions without reworking roles"
    ],
    impact: [
      "One consistent governance model across CLI, cloud and desktop",
      "A clear answer to who can do what, wherever an action starts",
      "A foundation for access control that can grow with the platform"
    ]
  },
  {
    id: 10,
    title: "Teaching an Agent to Think Like an Attacker",
    company: "Cosine",
    tags: ["Offensive Security", "AI Agents"],
    preview: "Built a remote offensive security agent for exploit verification and pentesting, shipped as a runnable GitHub workflow and a standalone binary.",
    context: "Security findings are only useful if you know they are real. Scanners surface potential vulnerabilities, but confirming whether something is actually exploitable has traditionally needed hands-on pentesting. Cosine needed an agent that could take on that offensive work remotely, and that fit into the way engineering teams already ship code.",
    challenge: [
      "Verify whether vulnerabilities are actually exploitable, not just theoretically present",
      "Run offensive security testing remotely, as an agent rather than a manual exercise",
      "Fit into existing developer workflows instead of adding another tool to manage",
      "Support teams that build on GitHub as well as those that need to run it elsewhere"
    ],
    approach: [
      "Implemented a remote offensive security agent for exploit verification and pentesting",
      "Packaged it as a runnable GitHub workflow so teams can trigger it alongside the rest of their pipeline",
      "Shipped it as a standalone binary for environments outside GitHub"
    ],
    impact: [
      "Exploit verification and pentesting available as part of the development workflow",
      "Two ways to run it: as a GitHub workflow or as a standalone binary",
      "Security findings that can be verified, not just reported"
    ]
  },
  {
    id: 1,
    title: "Building the Future of Private AI",
    company: "DataLove",
    location: "United Kingdom",
    tags: ["AI Strategy", "Product Development"],
    preview: "Architected RAG systems with sub-200ms latency across 30,000+ profiles. Zero data leakage. $0.04/user/month infrastructure costs.",
    context: "The AI industry has a fundamental trust problem. OpenAI, Google, Anthropic - they all need your data to improve their models. Users are forced to choose between powerful AI and data privacy. DataLove is building the third option: AI that's genuinely private, learns your context without extracting your data, and gives you full control. This requires rethinking how AI products work from the ground up - from vector databases to embedding pipelines to multi-cloud deployment strategies.",
    challenge: [
      "Design AI architecture where data flow is truly unidirectional - into your vault, never out",
      "Build RAG systems that enable semantic search across 30,000+ user profiles with sub-200ms query latency",
      "Implement tenant-specific encryption keys and namespace partitioning ensuring zero data leakage between enterprise clients",
      "Optimize embedding pipeline costs while maintaining quality - market APIs were too expensive at scale",
      "Design multi-cloud deployment balancing cost efficiency with performance and reliability requirements"
    ],
    approach: [
      "Architected RAG-based retrieval system using FastAPI, Qdrant vector DB, and distributed LLM inference (TGI/TEI)",
      "Built end-to-end data isolation architecture with tenant-specific encryption keys and namespace partitioning in Qdrant",
      "Defined product roadmap and wrote technical specs for embedding pipeline, hosted and benchmarked embedding models",
      "Designed multi-cloud deployment strategy across DigitalOcean (API layer) and RunPod (GPU inference)",
      "Created infrastructure architecture optimizing for both cost and 99.9% uptime SLA"
    ],
    impact: [
      "Enabled semantic search across 30,000+ user profiles with sub-200ms query latency",
      "Achieved zero data leakage between enterprise clients through robust isolation architecture",
      "Reduced embedding costs by 40% vs. using market APIs through self-hosted models",
      "Cut infrastructure costs to $0.04/user/month while maintaining 99.9% uptime SLA",
      "Potential applications in defense and high-security environments where data sovereignty is critical"
    ]
  },
  {
    id: 2,
    title: "Building Risk Intelligence from Scratch",
    company: "CGI",
    location: "United Kingdom",
    tags: ["Product Management", "Data Engineering"],
    preview: "Built Risk Intelligence MVP from scratch. Python ETL pipelines ingesting telemetry from 300,000 assets. Cut mean-time-to-detection by 65%.",
    context: "CGI needed a Risk Intelligence product that could ingest and analyze telemetry data from hundreds of thousands of assets in real-time. The challenge wasn't just building the tech - it was designing the entire data model, creating anomaly detection rules, and authoring the PRD for the Universal Data Recorder product. This required deep stakeholder engagement, mapping data flows across legacy systems, and prioritizing a backlog of 40+ features for engineering.",
    challenge: [
      "Design data model and build ETL pipelines to ingest telemetry from 300,000 assets",
      "Create anomaly detection rules that meaningfully reduce mean-time-to-detection",
      "Author PRD for Universal Data Recorder product with clear API contracts",
      "Conduct stakeholder interviews and map data flows across legacy systems",
      "Prioritize backlog of 40+ features for engineering team"
    ],
    approach: [
      "Built Risk Intelligence MVP from scratch: designed data model, wrote Python ETL pipelines",
      "Created anomaly detection rules based on telemetry patterns and historical incident data",
      "Authored PRD for Universal Data Recorder product, conducted 15+ stakeholder interviews",
      "Mapped data flows across legacy systems and defined API contracts for integrations",
      "Prioritized backlog of 40+ features for engineering based on business impact and technical feasibility"
    ],
    impact: [
      "Cut mean-time-to-detection by 65% through automated anomaly detection rules",
      "Successfully ingested telemetry from 300,000 assets via Python ETL pipelines",
      "Delivered PRD that aligned 15+ stakeholders on product direction",
      "Defined API contracts enabling seamless integration with legacy systems",
      "Established prioritization framework for 40+ feature backlog"
    ]
  },
  {
    id: 3,
    title: "When GDPR Becomes Your Product Blocker",
    company: "CGI",
    location: "United Kingdom",
    tags: ["Data Governance", "Vendor Management"],
    preview: "GDPR, CCPA, PIPL, LGPD - four different privacy regimes. Reduced audit prep time from 3 weeks to 4 days.",
    context: "The product needed to operate across multiple markets with different data governance requirements. GDPR was just the baseline - we also needed CCPA, PIPL, and LGPD compliance. Additionally, we needed to evaluate vendors for the observability stack, run POCs, and negotiate contracts. Without solving data governance and vendor selection, the product could not scale efficiently.",
    challenge: [
      "Create data governance playbook covering GDPR, CCPA, PIPL, and LGPD requirements",
      "Build compliance checklist tool that dramatically reduces audit prep time",
      "Lead vendor evaluation for observability stack across multiple vendors",
      "Run POCs and negotiate contract terms with favorable SLAs",
      "Deliver GTM strategy with TAM/SAM/SOM analysis and ROI model"
    ],
    approach: [
      "Created comprehensive data governance playbook covering GDPR, CCPA, PIPL, and LGPD",
      "Built compliance checklist tool automating audit preparation workflows",
      "Led vendor evaluation for observability stack, built scoring matrix, ran POCs with 3 vendors",
      "Negotiated contract terms focusing on cost reduction and SLA guarantees",
      "Delivered GTM strategy with TAM/SAM/SOM analysis and ROI model for executive decision-making"
    ],
    impact: [
      "Reduced audit prep time from 3 weeks to 4 days across client implementations",
      "Secured 20% cost reduction in vendor contracts with 24/7 SLA and penalty clauses",
      "Delivered ROI model projecting 30% reduction in operational incident costs",
      "Directly influenced executive decision to greenlight product investment",
      "Established reusable data governance framework for future international expansions"
    ]
  },
  {
    id: 4,
    title: "Migrating 100M Customers Without Breaking Production",
    company: "JPMorgan Chase",
    location: "India",
    tags: ["Cloud Migration", "Product Development"],
    preview: "Owned payments gateway for 100M+ users. 15-person cross-functional team. Shipped 12 features in 18 months. Reduced provisioning time from 2 weeks to 2 hours.",
    context: "JPMorgan was serving 100M+ customers on legacy data center infrastructure that was hitting capacity limits. The business demanded cloud migration for scalability and cost reduction, but finance is a zero-tolerance industry - any downtime means customer impact, regulatory scrutiny, and reputation damage. I owned the payments gateway product, wrote user stories, defined acceptance criteria, and ran sprint planning with a 15-person cross-functional team across US, UK, and India.",
    challenge: [
      "Own payments gateway product serving 100M+ users end-to-end",
      "Coordinate 15-person cross-functional team across US, UK, and India time zones",
      "Design and deploy AWS infrastructure using Terraform with regulatory compliance",
      "Define API specifications for merchant acquiring integrations",
      "Achieve 99.7% payment uptime during 2x transaction volume scaling"
    ],
    approach: [
      "Wrote user stories, defined acceptance criteria, ran sprint planning with cross-functional team",
      "Designed and deployed AWS infrastructure using Terraform: EC2 auto-scaling groups, RDS PostgreSQL clusters, CloudWatch monitoring",
      "Defined API specifications for merchant acquiring integrations using OpenAPI/Swagger",
      "Coordinated UAT with 8 external partners for merchant integrations",
      "Drove merchant onboarding improvements by mapping customer journey and identifying friction points"
    ],
    impact: [
      "Shipped 12 features in 18 months serving 100M+ customers",
      "Reduced provisioning time from 2 weeks to 2 hours through Infrastructure-as-Code",
      "Achieved 99.7% payment uptime during 2x transaction volume scaling",
      "Accelerated time-to-market by 25% for new merchant integrations",
      "Established cloud infrastructure patterns adopted by entire payments organization"
    ]
  },
  {
    id: 5,
    title: "Cutting Infrastructure Costs While Improving Performance",
    company: "JPMorgan Chase",
    location: "India",
    tags: ["Cost Optimization", "Data Analytics"],
    preview: "Led cloud cost optimization: analyzed CloudWatch metrics, right-sized 200+ EC2 instances. 33% cost reduction. $2.1M saved annually.",
    context: "After the cloud migration, finance demanded significant infrastructure cost reductions. The CFO wanted a 30%+ cut in spending. The platform was serving 100M+ customers with strict uptime requirements. I led the cloud cost optimization initiative, analyzing CloudWatch metrics, identifying idle resources with Python scripts, and right-sizing instances. Additionally, I built automated financial reporting pipelines and Tableau dashboards for P&L tracking.",
    challenge: [
      "Reduce infrastructure costs by 33% while maintaining performance",
      "Identify idle resources across hundreds of EC2 instances",
      "Build automated financial reporting pipeline for transaction analytics",
      "Create dashboards for P&L tracking and monthly close cycle",
      "Eliminate manual data entry errors in financial reporting"
    ],
    approach: [
      "Led cloud cost optimization initiative: analyzed CloudWatch metrics with Python scripts",
      "Identified idle resources and right-sized 200+ EC2 instances based on utilization patterns",
      "Built automated financial reporting pipeline: wrote SQL queries for transaction analytics",
      "Created Tableau dashboards for P&L tracking enabling real-time visibility",
      "Automated monthly close process eliminating manual data entry"
    ],
    impact: [
      "Delivered 33% annual infrastructure cost reduction ($2.1M saved)",
      "Right-sized 200+ EC2 instances based on CloudWatch metrics analysis",
      "Reduced monthly close cycle by 50% through automated reporting pipeline",
      "Eliminated manual data entry errors in financial reporting",
      "Established FinOps practices adopted across other payment platform teams"
    ]
  },
  {
    id: 6,
    title: "The Race for Market Share",
    company: "Sabre Corporation",
    location: "India",
    tags: ["Data Engineering", "Platform Modernization"],
    preview: "Engineered ETL pipelines automating finance reporting across 7 international markets. Reduced manual effort by 90%. Improved forecast accuracy from 85% to 97%.",
    context: "Sabre had finance reporting processes that were largely manual, spanning 7 international markets. The team needed automated ETL pipelines to improve accuracy and reduce manual effort. Additionally, they needed interactive dashboards tracking bookings, revenue, and customer retention to drive targeting strategy and improve customer engagement.",
    challenge: [
      "Automate finance reporting across 7 international markets",
      "Reduce manual effort in data processing and reporting",
      "Improve forecast accuracy from baseline 85%",
      "Build interactive dashboards for bookings, revenue, and customer retention",
      "Drive targeting strategy to increase customer engagement"
    ],
    approach: [
      "Engineered ETL pipelines in Python (pandas, SQL Alchemy) to automate finance reporting",
      "Built data processing workflows spanning 7 international markets",
      "Implemented data validation and quality checks improving forecast reliability",
      "Built interactive Angular dashboards with SQL-driven data models",
      "Created tracking for bookings, revenue, and customer retention metrics"
    ],
    impact: [
      "Reduced manual effort by 90% in finance reporting processes",
      "Improved forecast accuracy from 85% to 97%",
      "Automated reporting across 7 international markets",
      "Increased customer engagement by 50% through insights-driven targeting strategy",
      "Delivered interactive dashboards providing unprecedented visibility into business metrics"
    ]
  },
  {
    id: 7,
    title: "Building a Market for Battery Technology Training",
    company: "University of Birmingham",
    location: "United Kingdom",
    tags: ["Go-to-Market Strategy", "Revenue Modeling"],
    preview: "Technical training program ready. No clear target customers. Needed full GTM strategy, client segmentation, and revenue model from scratch.",
    context: "The University of Birmingham had developed a comprehensive battery technology training program with strong technical capabilities. However, they lacked clarity on who their target customers were, how to reach them, and what the business model should look like. They needed a complete go-to-market strategy to transform an academic program into a commercially viable offering in the rapidly growing battery and EV technology sector.",
    challenge: [
      "Identify and segment target customer groups for a technical training program",
      "Build a viable business model for an academic institution entering commercial markets",
      "Develop pricing strategy that balances accessibility with revenue generation",
      "Create a 10-year financial projection model with realistic growth assumptions",
      "Define competitive positioning in the emerging battery technology training market"
    ],
    approach: [
      "Conducted comprehensive market analysis of battery technology sector across automotive, energy storage, and manufacturing industries",
      "Segmented target customers into corporate training (OEMs, battery manufacturers), government upskilling programs, and individual professionals",
      "Developed detailed cost-benefit analysis for each customer segment with tailored value propositions",
      "Created 10-year revenue model with multiple pricing tiers, volume assumptions, and market penetration scenarios",
      "Designed complete GTM strategy including digital marketing, industry partnerships, and certification pathways"
    ],
    impact: [
      "Delivered actionable GTM strategy with clear customer segments and revenue pathways",
      "Projected sustainable revenue growth trajectory with break-even analysis and scaling milestones",
      "Identified partnership opportunities with major automotive and energy companies for B2B sales",
      "Established pricing framework balancing competitive positioning with margin requirements",
      "Positioned University of Birmingham as commercial leader in battery technology education"
    ]
  },
  {
    id: 8,
    title: "5,000 Teams. 48 Hours. Go.",
    company: "CODE19 Challenge",
    location: "India",
    tags: ["Rapid Prototyping", "AI Product Development"],
    preview: "Global hackathon. Overwhelmed hospitals. 5,000+ competitors. 48 hours to build something that could save lives.",
    context: "During the early pandemic, hospitals were overwhelmed with patients. A global hackathon was launched seeking any solution that could help people and hospitals during COVID. 5,000+ teams competing worldwide. The constraints: 48 hours to build and pitch a solution that could make a real impact. We developed an AI-powered COVID prescreening solution that could reduce unnecessary ER visits.",
    challenge: [
      "Develop a viable solution in 48 hours amid global competition",
      "Build a functional, demonstrable prototype with real clinical potential",
      "Validate approach without medical expertise on the team",
      "Create a credible go-to-market and distribution strategy",
      "Stand out among thousands of competing solutions with limited time"
    ],
    approach: [
      "Developed AI-powered prescreening tool to reduce unnecessary ER visits by helping patients assess symptoms",
      "Built functional prototype in 48 hours with focus on user experience and clinical accuracy",
      "Post-hackathon: Partnered with Stanford School of Medicine for clinical validation and trials",
      "Refined the solution based on medical expert feedback and real-world testing",
      "Pitched final validated solution to Stanford medical team"
    ],
    impact: [
      "Secured runner-up position among 5,000+ global teams",
      "Successfully sold the solution to Stanford School of Medicine for $5,000",
      "Achieved clinical validation from a top-tier medical institution",
      "Featured in Higher Education Digest for innovation during the pandemic",
      "Demonstrated ability to rapidly build and commercialize healthcare solutions under pressure"
    ],
    links: [
      {
        title: "Research Paper (arXiv)",
        url: "https://arxiv.org/abs/2007.00762"
      },
      {
        title: "News Article",
        url: "https://www.highereducationdigest.com/karnataka-students-bag-second-prize-at-code19-online-hackathon-against-covid19/"
      }
    ]
  }
];

// Each case study is presented as a mate-in-one puzzle. The positions are real and
// the solution is the only mating move, so chess players can actually solve them.
const puzzleMeta = {
  9: {
    theme: "Anastasia's mate",
    fen: '8/4N1pk/8/8/8/3R4/8/6K1 w - - 0 1',
    solution: { from: 'd3', to: 'h3' },
    san: 'Rh3#',
    note: 'The knight already guards the side doors; the rook closes the last route. Every way in goes through the same gate.',
    headline: { value: 'One model', label: 'for governance across CLI, cloud and desktop' },
  },
  10: {
    theme: 'Double check',
    fen: 'r2qkb1r/pppp1ppp/8/8/4N3/8/PPP2PPP/4R1K1 w - - 0 1',
    solution: { from: 'e4', to: 'f6' },
    san: 'Nf6#',
    note: 'Move the knight and the rook behind it strikes too. Two attacks at once, so no single defence holds: the attacker\'s mindset.',
    headline: { value: 'Workflow + binary', label: 'runnable on GitHub or anywhere else' },
  },
  1: {
    theme: 'Back-rank mate',
    fen: '6k1/5ppp/8/8/8/8/5PPP/4R1K1 w - - 0 1',
    solution: { from: 'e1', to: 'e8' },
    san: 'Re8#',
    note: 'The rook seals the eighth rank. Nothing gets in, nothing gets out: exactly how a private data vault should work.',
    headline: { value: '$0.04', label: 'infrastructure cost per user per month' },
  },
  2: {
    theme: 'Smothered mate',
    fen: '6rk/6pp/8/6N1/8/8/8/6K1 w - - 0 1',
    solution: { from: 'g5', to: 'f7' },
    san: 'Nf7#',
    note: 'One precise knight jump finds the weakness the other pieces are hiding. Detection, not brute force.',
    headline: { value: '−65%', label: 'mean-time-to-detection' },
  },
  3: {
    theme: 'King & queen mate',
    fen: 'k7/7Q/1K6/8/8/8/8/8 w - - 0 1',
    solution: { from: 'h7', to: 'b7' },
    san: 'Qb7#',
    note: 'The queen only works because the king has her back. Governance and execution, moving together.',
    headline: { value: '3w → 4d', label: 'audit prep time' },
  },
  4: {
    theme: 'Ladder mate',
    fen: '7k/R7/8/8/8/8/8/1R2K3 w - - 0 1',
    solution: { from: 'b1', to: 'b8' },
    san: 'Rb8#',
    note: 'Two rooks climb rank by rank, never leaving a gap. That is how you move 100M customers without breaking production.',
    headline: { value: '99.7%', label: 'payment uptime at 2x volume' },
  },
  5: {
    theme: 'Queen & bishop battery',
    fen: '5rk1/5pp1/8/7Q/8/3B4/5PPP/6K1 w - - 0 1',
    solution: { from: 'h5', to: 'h7' },
    san: 'Qh7#',
    note: 'The bishop has been quietly supporting from a distance. Data first, then the decisive move.',
    headline: { value: '$2.1M', label: 'saved annually' },
  },
  6: {
    theme: 'Long-diagonal mate',
    fen: '6k1/5p1p/8/8/3Q4/8/1B3PPP/6K1 w - - 0 1',
    solution: { from: 'd4', to: 'g7' },
    san: 'Qg7#',
    note: 'A clean line from corner to corner. Automate the pipeline and the whole board opens up.',
    headline: { value: '85% → 97%', label: 'forecast accuracy' },
  },
  7: {
    theme: 'Arabian mate',
    fen: '7k/1R6/5N2/8/8/8/8/6K1 w - - 0 1',
    solution: { from: 'b7', to: 'h7' },
    san: 'Rh7#',
    note: 'Rook and knight cover what neither can alone. Segments and pricing, working as one plan.',
    headline: { value: '10-year', label: 'revenue model & GTM plan' },
  },
  8: {
    theme: 'Promotion',
    fen: '7k/1P4pp/8/8/8/8/8/6K1 w - - 0 1',
    solution: { from: 'b7', to: 'b8', promo: 'q' },
    san: 'b8=Q#',
    note: 'A humble pawn walks the whole board and becomes a queen. 48 hours, from hackathon idea to Stanford.',
    headline: { value: 'Runner-up', label: 'of 5,000+ global teams' },
  },
};

export const scenarios = baseScenarios.map((s) => ({ ...s, ...puzzleMeta[s.id] }));

export const experiences = [
  {
    title: "Software Engineer",
    company: "Cosine",
    period: "Jul 2026 - Present",
    highlights: [
      "Created an RBAC model for Cosine for governance across all three planes: CLI, cloud and desktop",
      "Implemented a remote offensive security agent for exploit verification and pentesting, as a runnable GitHub workflow and standalone binary"
    ]
  },
  {
    title: "AI Strategy Consultant",
    company: "DataLove",
    location: "United Kingdom",
    period: "Oct 2025 - Jul 2026",
    highlights: [
      "Architected RAG-based retrieval system using FastAPI, Qdrant vector DB, and distributed LLM inference (TGI/TEI) enabling semantic search across 30,000+ user profiles with sub-200ms query latency",
      "Built end-to-end data isolation architecture with tenant-specific encryption keys and namespace partitioning in Qdrant, ensuring zero data leakage between enterprise clients",
      "Defined product roadmap and wrote technical specs for embedding pipeline, hosted embedding models, benchmarked against alternatives, reducing embedding costs by 40% vs. market APIs",
      "Designed multi-cloud deployment strategy across DigitalOcean (API layer) and RunPod (GPU inference), cutting infrastructure costs to $0.04/user/month while maintaining 99.9% uptime SLA"
    ]
  },
  {
    title: "MBA Product Intern",
    company: "CGI",
    location: "United Kingdom",
    period: "Jun 2025 - Sep 2025",
    highlights: [
      "Built Risk Intelligence MVP from scratch: designed data model, wrote Python ETL pipelines to ingest telemetry from 300,000 assets, created anomaly detection rules that cut mean-time-to-detection by 65%",
      "Authored PRD for Universal Data Recorder product, conducted 15+ stakeholder interviews, mapped data flows across legacy systems, defined API contracts, and prioritized backlog of 40+ features",
      "Created data governance playbook covering GDPR, CCPA, PIPL, and LGPD; built compliance checklist tool that reduced audit prep time from 3 weeks to 4 days",
      "Led vendor evaluation for observability stack, ran POCs with 3 vendors, negotiated contract terms securing 20% cost reduction and 24/7 SLA with penalty clauses",
      "Delivered GTM strategy with TAM/SAM/SOM analysis and ROI model projecting 30% reduction in operational incident costs"
    ]
  },
  {
    title: "Associate: Product & Cloud Platform",
    company: "JPMorgan Chase",
    location: "India",
    period: "Jul 2022 - Jul 2024",
    highlights: [
      "Owned payments gateway product serving 100M+ users, wrote user stories, defined acceptance criteria, ran sprint planning with 15-person cross-functional team across US, UK, and India, shipped 12 features in 18 months",
      "Designed and deployed AWS infrastructure using Terraform, provisioned EC2 auto-scaling groups, RDS PostgreSQL clusters, and CloudWatch monitoring; reduced provisioning time from 2 weeks to 2 hours",
      "Led cloud cost optimization initiative: analyzed CloudWatch metrics, identified idle resources with Python scripts, right-sized 200+ EC2 instances, delivered 33% annual infrastructure cost reduction ($2.1M saved)",
      "Defined API specifications for merchant acquiring integrations using OpenAPI/Swagger; coordinated UAT with 8 external partners, achieving 99.7% payment uptime during 2x transaction volume scaling",
      "Built automated financial reporting pipeline: wrote SQL queries for transaction analytics, created Tableau dashboards for P&L tracking, reduced monthly close cycle by 50%"
    ]
  },
  {
    title: "Software Engineer",
    company: "Sabre Corporation",
    location: "India",
    period: "Feb 2021 - Jul 2022",
    highlights: [
      "Engineered ETL pipelines in Python (pandas, SQL Alchemy) to automate finance reporting across 7 international markets, reduced manual effort by 90% and improved forecast accuracy from 85% to 97%",
      "Built interactive Angular dashboards with SQL-driven data models tracking bookings, revenue, and customer retention; insights drove targeting strategy that increased customer engagement by 50%"
    ]
  }
];

export const education = [
  {
    degree: "MBA",
    school: "Warwick Business School",
    location: "United Kingdom",
    period: "Sep 2024 - Sep 2025",
    highlights: [
      "Vice President, A.I. Circle: Organized 10+ events on AI product strategy, hosted events in collaboration with Google",
      "BCG Unlock: Selected for competitive program; delivered strategic recommendations for financial services client"
    ]
  },
  {
    degree: "BTech, Electrical & Electronics Engineering",
    school: "Manipal University",
    location: "India",
    period: "Jul 2017 - Jul 2021",
    highlights: [
      "Core Technical Skills: TypeScript, Python, NodeJS, AWS (Cloud Architecture, Lambda, Kinesis, S3), Terraform, REST APIs, Payments Systems, PostgreSQL, Data Analytics, ML"
    ]
  }
];

// The career, played as an Italian Game. Each white move is one role; black's reply follows.
export const careerMoves = [
  {
    n: 1, white: 'e4', black: 'e5',
    plies: [{ from: 'e2', to: 'e4' }, { from: 'e7', to: 'e5' }],
    ref: 'Sabre Corporation',
    idea: 'Claim the centre. Real systems, real data, seven markets.',
  },
  {
    n: 2, white: 'Nf3', black: 'Nc6',
    plies: [{ from: 'g1', to: 'f3' }, { from: 'b8', to: 'c6' }],
    ref: 'JPMorgan Chase',
    idea: 'Develop with purpose: payments at 100M+ scale.',
  },
  {
    n: 3, white: 'Bc4', black: 'Bc5',
    plies: [{ from: 'f1', to: 'c4' }, { from: 'f8', to: 'c5' }],
    ref: 'CGI',
    idea: 'Aim at the weak point: product, data governance and go-to-market in one summer.',
  },
  {
    n: 4, white: 'O-O', black: 'Nf6',
    plies: [
      { from: 'e1', to: 'g1', rook: { from: 'h1', to: 'f1' } },
      { from: 'g8', to: 'f6' },
    ],
    ref: 'DataLove',
    idea: 'Castle. Keep the king safe: AI that never lets your data leave the vault.',
  },
  {
    n: 5, white: 'Re1', black: 'd6',
    plies: [{ from: 'f1', to: 'e1' }, { from: 'd7', to: 'd6' }],
    ref: 'Cosine',
    idea: 'Rook to the open file. Guard every line in and test every line of attack.',
  },
];
